import { Link, useParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";
import AnimeCard from "@/components/base/AnimeCard";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { characters } from "@/mocks/characters";
import { findAnime, findCharacter, getAnimeCharacters } from "@/lib/data";

export default function CharacterPage() {
  const { id } = useParams();
  const character = findCharacter(id);

  useDocumentMeta({
    title: character ? `日漫 - ${character.name} 详细介绍` : "日漫 - 角色资料",
    description: character
      ? `${character.name}（${character.nameJa}）出自《${character.animeTitle}》。性格简介：${character.personality}`
      : "日漫角色资料页，查看角色的性格、故事线与人物关系。",
    keywords: character ? `${character.name},${character.animeTitle},动漫角色,日漫` : "动漫角色,日漫",
  });

  if (!character) {
    return (
      <Layout>
        <div className="w-full px-4 md:px-6 py-20">
          <EmptyState title="未找到该角色" description="角色信息可能已变更，去角色总览看看其他人物吧。" />
          <div className="mt-6 text-center">
            <Link
              to="/characters"
              className="inline-flex items-center gap-2 px-5 h-11 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line" />
              返回角色总览
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  const anime = findAnime(character.animeId);
  const siblings = getAnimeCharacters(character.animeId).filter((item) => item.id !== character.id);

  return (
    <Layout>
      <div className="w-full px-4 md:px-6 pt-6">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: "角色资料", to: "/characters" },
            { label: character.name },
          ]}
        />
      </div>

      <section className="w-full px-4 md:px-6 py-6">
        <div className="rounded-lg border border-background-200 bg-background-50 overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-12">
            <div className="lg:col-span-4 bg-secondary-50 p-6 md:p-8 flex flex-col items-center">
              <div className="w-48 h-48 md:w-56 md:h-56 rounded-full overflow-hidden border-4 border-background-50 bg-background-100">
                <img
                  src={character.avatar}
                  alt={`${character.name} 角色形象`}
                  title={`${character.name} 动漫角色资料`}
                  className="w-full h-full object-top"
                />
              </div>
              <h1 className="mt-5 font-heading text-2xl md:text-3xl font-extrabold text-foreground-950 text-center">
                {character.name}
              </h1>
              <p className="mt-1 text-sm text-foreground-500">{character.nameJa}</p>
              <div className="mt-4 flex flex-wrap justify-center gap-2">
                {character.tags.map((tag) => (
                  <span key={tag} className="px-2.5 py-1 rounded-md bg-primary-100 text-primary-700 text-xs">
                    {tag}
                  </span>
                ))}
              </div>
              {anime && (
                <Link
                  to={`/detail/${anime.id}`}
                  className="mt-5 inline-flex items-center gap-2 px-5 h-10 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-movie-2-line" />
                  《{anime.title}》
                </Link>
              )}
            </div>

            <div className="lg:col-span-8 p-6 md:p-8 space-y-7">
              <div>
                <h2 className="font-heading text-lg font-bold text-foreground-950 mb-2 flex items-center gap-2">
                  <i className="ri-user-heart-line text-primary-500" />
                  性格简介
                </h2>
                <p className="text-sm md:text-base leading-relaxed text-foreground-700">{character.personality}</p>
              </div>

              <div>
                <h2 className="font-heading text-lg font-bold text-foreground-950 mb-2 flex items-center gap-2">
                  <i className="ri-book-open-line text-primary-500" />
                  主要故事线
                </h2>
                <p className="text-sm md:text-base leading-relaxed text-foreground-700">{character.story}</p>
              </div>

              <div>
                <h2 className="font-heading text-lg font-bold text-foreground-950 mb-3 flex items-center gap-2">
                  <i className="ri-links-line text-primary-500" />
                  人物关系
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {character.relations.map((relation) => (
                    <div
                      key={relation.name}
                      className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-100 p-3"
                    >
                      <span className="w-9 h-9 shrink-0 rounded-full bg-secondary-100 text-secondary-700 flex items-center justify-center">
                        <i className="ri-user-3-line" />
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-foreground-950 line-clamp-1">{relation.name}</p>
                        <p className="text-xs text-foreground-600 line-clamp-1">{relation.relation}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {siblings.length > 0 && (
        <section className="w-full px-4 md:px-6 pb-8">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-5">同作品其他角色</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {siblings.map((item) => (
              <Link
                key={item.id}
                to={`/character/${item.id}`}
                className="group flex flex-col rounded-lg border border-background-200 bg-background-50 overflow-hidden hover:border-primary-300 transition-colors cursor-pointer"
              >
                <div className="w-full aspect-square overflow-hidden bg-background-100">
                  <img
                    src={item.avatar}
                    alt={`${item.name} 角色头像`}
                    title={`${item.name} 角色介绍`}
                    className="w-full h-full object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-4">
                  <h3 className="font-heading text-sm font-bold text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-1">
                    {item.name}
                  </h3>
                  <p className="mt-1 text-xs text-foreground-600 line-clamp-2">{item.personality}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {anime && (
        <section className="w-full px-4 md:px-6 pb-16">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-5">相关动画推荐</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {characters
              .filter((item) => item.animeId !== character.animeId)
              .slice(0, 4)
              .map((item) => {
                const rec = findAnime(item.animeId);
                return rec ? <AnimeCard key={rec.id} anime={rec} /> : null;
              })}
          </div>
        </section>
      )}
    </Layout>
  );
}